"use client"

import { useEffect, useRef } from "react"
import { initPostHog, getExperimentId, getBootstrapFlags } from "@/lib/posthog"

/**
 * Client bootstrap for PostHog — initializes the SDK and bootstraps identity
 * from the experiment ID cookie. Experiment hooks read the singleton directly
 * from `@/lib/posthog` (not posthog-js/react context).
 *
 * `instrumentation-client.ts` schedules this same `initPostHog()` at idle for
 * pages that never hydrate (the not-found surfaces); whichever runs second is
 * a no-op via the `posthog.__loaded` guard, so this effect is unchanged.
 *
 * SPA pageview tracking runs inside the PostHog SDK itself
 * (`capture_pageview: 'history_change'` in `lib/posthog.ts`), so this
 * component does NOT call `usePathname()`. That matters under Next 16
 * `cacheComponents: true`: calling `usePathname()` here would mark the
 * entire wrapped subtree (i.e. `{children}` = every page body) as dynamic
 * and force it to stream per-request after the static shell.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const experimentId = getExperimentId()
    const bootstrapFlags = getBootstrapFlags()
    // Defer PostHog init until the browser is idle so its ~112 KB bundle
    // parse + the singleton's initial config sweep doesn't compete with
    // LCP-critical work on mobile. PostHog buffers events fired before
    // init() returns, so any `track.*` calls between mount and idle are
    // queued and flush on the first subsequent `capture()`.
    const idle =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (cb: () => void) => window.requestIdleCallback(cb, { timeout: 3000 })
        : (cb: () => void) => window.setTimeout(cb, 2000)
    idle(() => {
      initPostHog(experimentId, bootstrapFlags)
    })
  }, [])

  return <>{children}</>
}
